import React, { useCallback, useEffect, useState } from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { fetchAlerts as fetchAlertsAction, fetchUpdatedAlert as fetchUpdatedAlertAction } from './alertActions';
import {
  fetchReasons as fetchReasonsAction,
  fetchReasonsV2 as fetchReasonsV2Action,
} from '../scenario/scenariosActions';
import { AppState } from '../store/store';
import AlertRowAssignment from './AlertRowAssignment';
import './Alerts.css';
import Table from '../table/Table';
import { ApiSortDirection } from '../api';
import { getUserId, getUserName } from '../auth/authReducer';
import { setPage } from './alertReducer';
import { isNavigableCellClicked } from '../common/helper';
import FormattedDateTime from '../common/datetime/FormattedDateTime';
import AlertDetailsPopover from './AlertDetailsPopover';
import TransactionDetailsPopover from '../transaction/TransactionDetailsPopover';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';
import { PersonLink } from '../common/PersonLink';
import { ALL_VALUE } from './helper';
import TransactionAlertsFilter from './TransactionAlertsFilter';
import { apiScenarioTypeToFetchForTransactionBacklog } from './scenarioTypeHelper';
import { apiStatusesToFetchForTransactionBacklog } from './alertStatusHelper';
import { reasonIdsToFetchForTransactionAlertsBacklog } from './reasonsHelper';
import { assigneeIdToFetchForTransactionAlertsBacklog } from './assigneeHelper';
import { useNavigate } from 'react-router';
import { fetchAlertStatuses } from '../screening/alert-status/alertStatusesActions';
import { InternalApiEntityType, InternalApiMonitoringAlert, InternalApiSortDirection } from '../api-internal';
import { MonitoringAlertHistory } from './AlertHistory';
import { useAlertsLiveUpdates } from './useAlertsLiveUpdates';
import { useMe } from '../organisation/useMe';
import SalvPage from '../common/SalvPage';
import { useQueryAttributeMappings } from '../schema/mappings/mappingsActions';
import DataLoadingErrorFallback from '../common/errors/DataLoadingErrorFallback';
import { SalvProgressCircle } from '@salvapp/ui-components';

export const TransactionAlerts = ({
  page,
  size,
  total,
  alerts,
  allStatuses,
  fetchAlerts,
  fetchUpdatedAlert,
  setPage,
  filters,
  reasons,
  fetchAlertStatuses,
}: ComponentProps) => {
  const navigate = useNavigate();
  const navigateToCustomer = (id: string) => navigate(`/customer/${id}`);
  const navigateToMonitoringAlert = (id: number) => navigate(`/monitoring/alerts/${id}`);
  const { features } = useMe();

  const [fetching, setFetching] = useState(false);
  const [failedToLoadAlerts, setFailedToLoadAlerts] = useState(false);

  const params = new URLSearchParams(location.search);
  const [orderBy, setOrderBy] = useState({
    attribute: params.get('sortBy') || 'Time',
    direction: (params.get('sortOrder') as ApiSortDirection) || ApiSortDirection.DESC,
  });
  const updateOrderBy = (value: { attribute: string; direction: ApiSortDirection }) => {
    const params = new URLSearchParams(location.search);
    params.set('sortBy', value.attribute);
    params.set('sortOrder', value.direction);
    navigate({
      search: `?${params.toString()}`,
    });
    setOrderBy(value);
  };

  const fetchNewAlerts = useCallback(
    async ({ background = false }: { background?: boolean } = {}) => {
      if (!background) {
        setFetching(true);
        setFailedToLoadAlerts(false);
      }

      if (allStatuses.length == 0) {
        await fetchAlertStatuses();
        return;
      }

      try {
        await fetchAlerts(
          page,
          size,
          undefined,
          apiStatusesToFetchForTransactionBacklog(filters.selectedStatus, allStatuses),
          apiScenarioTypeToFetchForTransactionBacklog(filters.selectedScenarioType),
          assigneeIdToFetchForTransactionAlertsBacklog(filters.selectedAssignee),
          undefined, // Transactions monitoring page uses only V2.
          reasonIdsToFetchForTransactionAlertsBacklog(reasons, filters.selectedMultiReason),
          undefined,
          orderBy.attribute,
          orderBy.direction && InternalApiSortDirection[orderBy.direction],
          InternalApiEntityType.TRANSACTION
        );
        setFailedToLoadAlerts(false);
      } catch (_) {
        setFailedToLoadAlerts(true);
      }
      if (!background) {
        setFetching(false);
      }
    },
    [
      allStatuses,
      page,
      size,
      fetchAlerts,
      fetchAlertStatuses,
      filters.selectedStatus,
      filters.selectedAssignee,
      filters.selectedReason,
      filters.selectedMultiReason,
      filters.selectedScenarioType,
      orderBy,
      JSON.stringify(reasons),
    ]
  );

  useEffect(() => {
    if (reasons.length !== 1) {
      // Renders only after reasons have been loaded. Could be optimized to fetch immediately if no scenario params exist.
      fetchNewAlerts();
    }
  }, [fetchNewAlerts]);

  useAlertsLiveUpdates({
    fetchUpdatedAlert,
    // Mirror the mount guard: don't poll until scenario reasons have loaded.
    refreshAlerts: () => (reasons.length !== 1 ? fetchNewAlerts({ background: true }) : undefined),
  });

  const navigateTo = (alert: InternalApiMonitoringAlert) => {
    if (isFeatureEnabled(ORGANISATION_FEATURE.MONITORING_ALERT_PAGE, features)) {
      return navigateToMonitoringAlert(alert.id);
    }
    return navigateToCustomer(alert.person.id);
  };

  const {
    data: attributeMappings,
    isPending: isAttributeMappingsPending,
    isError: isAttributeMappingsError,
  } = useQueryAttributeMappings();

  if (isAttributeMappingsError) {
    return <DataLoadingErrorFallback message={'Could not load Attribute Mappings'} />;
  } else if (isAttributeMappingsPending) {
    return <SalvProgressCircle ariaLabel={'Loading Attribute Mappings'} />;
  }

  const renderAlerts = () => {
    return alerts.map((alert: InternalApiMonitoringAlert) => (
      <tr
        key={alert.id}
        onClick={(event: any) => {
          isNavigableCellClicked(event) && navigateTo(alert);
        }}
        className='clickable-row'
      >
        <td>
          <PersonLink person={alert.person} mappings={attributeMappings.map} />
        </td>
        <td>
          <FormattedDateTime date={alert.createdTime} showTime />
        </td>
        <td>
          <TransactionDetailsPopover transaction={alert.transaction} showAmount={true} />
        </td>
        <td className='alert-details'>
          <AlertDetailsPopover alert={alert} />
        </td>
        <td>
          <AlertRowAssignment alert={alert} />
        </td>
        <td>{allStatuses.find((status) => status.id === alert.statusId)?.textName}</td>
        <td>
          <MonitoringAlertHistory alert={alert} />
        </td>
      </tr>
    ));
  };

  return (
    <SalvPage title={'Transaction monitoring alerts'}>
      <TransactionAlertsFilter />
      <Table
        responsive={false}
        total={total}
        page={page}
        size={size}
        items={alerts}
        fetchPage={setPage}
        attributes={[
          { key: 'Person', sortable: false },
          { key: 'Time', sortable: true },
          { key: 'Transaction', sortable: true },
          { key: 'Details', sortable: false },
          { key: 'Assignee', sortable: false },
          { key: 'Status', sortable: false },
          { key: 'Actions', sortable: false },
        ]}
        body={renderAlerts}
        onSort={(attribute, direction) => {
          updateOrderBy({ attribute, direction });
        }}
        fetching={fetching}
        emptyMessage={failedToLoadAlerts ? 'Failed to load alerts. Please try again.' : ''}
      />
    </SalvPage>
  );
};

const mapStateToProps = (state: AppState) => {
  const { alerts, scenarios, filters, screeningAlertStatuses } = state;

  return {
    ...alerts,
    allStatuses: screeningAlertStatuses.statuses,
    currentUser: {
      key: getUserName(state) || ALL_VALUE,
      value: String(getUserId(state) || ALL_VALUE),
      label: getUserName(state) || 'All',
    },
    reasons: [
      {
        key: ALL_VALUE,
        value: ALL_VALUE,
        label: 'All reasons',
      },
    ]
      .concat(
        scenarios.reasons.map((reason) => ({
          key: reason,
          value: reason,
          label: reason,
        }))
      )
      .concat(
        scenarios.reasonsWithScenarioIDs.map((reason) => ({
          key: reason.name,
          value: reason.scenarioIds.join(','),
          label: reason.name,
        }))
      ),
    filters: filters,
  };
};

const mapDispatchToProps = {
  fetchAlerts: fetchAlertsAction,
  fetchUpdatedAlert: fetchUpdatedAlertAction,
  fetchReasons: fetchReasonsAction,
  fetchReasonsV2: fetchReasonsV2Action,
  fetchAlertStatuses,
  setPage,
};

type ComponentProps = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export default connect(mapStateToProps, mapDispatchToProps)(TransactionAlerts);
